import { ContactInfo, useAuth, useContact, useOidc } from '@/context';
import { NewsRibbonWrapper } from '@/context/NewsRibbon';
import {
  NEWS_RIBBON_BOX_PORTAL_EVENT,
  NEWS_RIBBON_BOX_PORTAL_ID,
} from '@/context/NewsRibbon/NewsRibbon.constants';
import { usePageModels } from '@/context/PageModels';
import { NewsRibbon } from '@/features/NewsRibbon';
import {
  PORTAL_NAV_BOX_HEADER_EVENT,
  PORTAL_NAV_BOX_HEADER_ID,
} from '@/features/PortalNavigation/PortalNavigation.constants';
import { MessageBar } from '@/features/shared';
import { withGTM, withPortalBoxFeatures } from '@/features/shared/HOCs';
import { usePortalBoxHandler } from '@/features/shared/HOCs/withPortalBoxFeatures';
import { useDocumentContent } from '@/features/shared/Hooks';
import { useTranslatedLanguageList } from '@/hooks/useTranslatedLanguageList';
import { useIsPortal } from './hooks/useIsPortal';
import { hashObject } from '@/utils/helpers';
import {
  resolveBlacklistUrls,
  resolveBool,
  resolveCRMSyncs,
  resolveImage,
  resolveLink,
  resolveRTC,
} from '@/utils/resolvers';
import { BrManageContentButton } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import { pick } from 'lodash/fp';
import React, { FunctionComponent, memo, useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TagManager from 'react-gtm-module';
import { useStorageState } from 'react-storage-hooks';
import GlobalHeader from './GlobalHeader';
import { Props as GlobalHeaderProps } from './GlobalHeader/GlobalHeader.interface';
import {
  onDatalayerClickCTA,
  onDatalayerClickLegacy,
  onDatalayerClickLink,
  onDatalayerCustom,
} from './GlobalHeaderContainer.datalayer';
import { NEWS_RIBBON_MAPPER } from './GlobalHeaderContainer.mapper';
import useStyles from './GlobalHeaderContainer.style';
import AccountSectionResolver from './resolvers/AccountSectionResolver';
import AppsSectionResolver from './resolvers/AppsSectionResolver';
import { isPortalPathname } from '@/utils/portal';

const ContentButton = memo(
  ({ slotName, document, className }: { slotName: string; document: any; className: any }) => {
    return (
      <div key={slotName} className={className} slot={slotName}>
        {document && <BrManageContentButton content={document} />}
      </div>
    );
  },
);

const PortalNavigationBox = withPortalBoxFeatures((props) => <div {...props}></div>, {
  boxId: PORTAL_NAV_BOX_HEADER_ID,
  readyEventName: PORTAL_NAV_BOX_HEADER_EVENT,
});

const GlobalHeaderContainer: FunctionComponent<Br.Props> = ({ component, page }: Br.Props) => {
  const pageModels = usePageModels();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const { logIn, logOut } = useAuth();
  const { contactInfo, registrationStatus } = useContact();
  const { userData } = useOidc();
  const [, setLastCustomUrl] = useStorageState<string>(sessionStorage, 'SEO.LAST_CUSTOM_URL', '');
  const [, setIsLoggingOutAttempt] = useStorageState<boolean>(
    sessionStorage,
    'DATALAYER.IS_LOGGING_OUT_ATTEMPT',
    false,
  );
  const [, setIsLoggingInAttempt] = useStorageState<boolean>(
    sessionStorage,
    'DATALAYER.IS_LOGGING_IN_ATTEMPT',
    false,
  );

  const {
    componentParameterMap,
    'language-switcher': defaultLanguageList,
    'portal-language-switcher': portalLanguageList,
    document: documentRef,
    portalDocument: portalDocumentRef,
    loginDocument: loginDocumentRef,
    'globalHeader.config': config,
    'globalHeader.labels': labels,
    contactUsDocument: marketoDocumentRef,
    portalContactUsDocument: portalMarketoDocumentRef,
    newsRibbonDocument: ribbonDocumentRef,
    myAccountDocument: myAccountDocumentRef,
    appsDocument: appsDocumentRef,
    ctaDocument: ctaDocumentRef,
    portalCtaDocument: portalCtaDocumentRef,
    title: newsRibbonTitle,
    searchContentDocument: searchContentDocumentRef,
  } = useMemo(() => component.getModels(), [component]);

  const isLoggedIn = React.useMemo(() => !!Object.keys(contactInfo || {}).length, [contactInfo]);

  const userSessionInfo = React.useMemo<ContactInfo | null>(() => {
    if (contactInfo) return contactInfo;

    return userData
      ? {
          firstName: userData.profile.given_name || '',
          lastName: userData.profile.family_name || '',
          fullName: userData.profile.name || '',
        }
      : null;
  }, [contactInfo, userData]);

  const triggerCtaDatalayer = (eventLabel: string, type: string) => {
    const dataLayer = {
      event: 'trackEvent-ctaClicked',
      event_name: 'cta_click',
      eventCategory: 'engagement',
      eventAction: 'cta_click',
      eventLabel,
      position: 'primary_nav',
      link_text: eventLabel,
      type,
    };
    TagManager.dataLayer({ dataLayer });
  };

  const handleLoginClicked = useCallback(() => {
    triggerCtaDatalayer(labels.login_label, 'login');
    setIsLoggingInAttempt(true);
    setLastCustomUrl('');
    logIn();
  }, [labels.login_label, logIn, setIsLoggingInAttempt, setLastCustomUrl]);

  const handleLogoutClicked = useCallback(() => {
    triggerCtaDatalayer(labels.logout_label, 'logout');
    setIsLoggingOutAttempt(true);
    setLastCustomUrl('');
    logOut();
  }, [labels.logout_label, logOut, setIsLoggingOutAttempt, setLastCustomUrl]);

  const classes = useStyles();

  const {
    config: globalConfig,
    labels: { marketo: marketoLabels },
    languageConfig,
  } = pageModels;

  const { marketo: marketoConfig, elasticsearch: elasticsearchConfig } = globalConfig;

  const isPortal = useIsPortal();

  const listDocument = useMemo(() => {
    if (!documentRef && !portalDocumentRef) return;

    if (isPortal) {
      return portalDocumentRef && page?.getContent(portalDocumentRef);
    }

    return documentRef && page?.getContent(documentRef);
  }, [documentRef, portalDocumentRef, page, isPortal]);
  const marketoDocument = useMemo(() => {
    if (isPortal) {
      return portalMarketoDocumentRef && page?.getContent(portalMarketoDocumentRef);
    }

    return marketoDocumentRef && page?.getContent(marketoDocumentRef);
  }, [marketoDocumentRef, portalMarketoDocumentRef, page, isPortal]);
  const ribbonDocument = useMemo(
    () => ribbonDocumentRef && page?.getContent(ribbonDocumentRef),
    [page, ribbonDocumentRef],
  );
  const loginDocument = useMemo(
    () => loginDocumentRef && page?.getContent(loginDocumentRef),
    [loginDocumentRef, page],
  );

  const { languageList } = useTranslatedLanguageList(defaultLanguageList, portalLanguageList);

  // eslint-disable-next-line no-underscore-dangle
  const pageTemplate = useMemo(() => page?.getComponent()?.getParameters()?.template ?? '', [page]);
  const isPreview = useMemo(() => page?.isPreview(), [page]);
  const isBlogDetail = useMemo(() => /blog-detail/.test(pageTemplate), [pageTemplate]);
  const isTransparent = useMemo(
    () => /single-column-transparent-nav/.test(pageTemplate),
    [pageTemplate],
  );

  const { contentItem: menuListRef } = useMemo(
    () => (listDocument?.getData() as any) || {},
    [listDocument],
  );
  const marketoData = useMemo(() => (marketoDocument?.getData() as any) || {}, [marketoDocument]);
  const ribbonData = useMemo(() => (ribbonDocument?.getData() as any) || {}, [ribbonDocument]);
  const { menuGroup: loginGroupRef } = useMemo(
    () => (loginDocument?.getData() as any) || {},
    [loginDocument],
  );

  const [myAccountData] = useDocumentContent(page, myAccountDocumentRef);
  const [appsDocumentData] = useDocumentContent(page, appsDocumentRef);
  const [searchContentDocumentData] = useDocumentContent(page, searchContentDocumentRef);
  const [ctaDocumentData] = useDocumentContent(page, ctaDocumentRef);
  const [portalCtaDocumentData] = useDocumentContent(page, portalCtaDocumentRef);
  const sectionDocuments = useRef<any[]>([]);

  const globalHeaderProps: GlobalHeaderProps | undefined = useMemo(() => {
    sectionDocuments.current = [];
    return {
      accountSection: AccountSectionResolver({ rawVal: myAccountData, page, pageModels }),
      appsSection: AppsSectionResolver(appsDocumentData, page),
      loginSection:
        loginGroupRef &&
        loginGroupRef.map((group: any) => {
          const { verticalDivider, contentList } = group;
          return {
            verticalDivider,
            menuGroupList: contentList.map((content: any) => {
              const { columnTitleDescription, primaryNavigationColumnTitle, variant, menuItem } =
                content;
              return {
                columnTitleDescription: resolveRTC({ rawVal: columnTitleDescription }),
                primaryNavigationColumnTitle: resolveLink({
                  rawVal: primaryNavigationColumnTitle,
                  page,
                  pageModels,
                }),
                variant: variant && variant.replace(/"/g, ''),
                menuItem: menuItem.length
                  ? menuItem.map((item: any) => {
                      const { image, link, menuDescription, linkStyle } = item;

                      return {
                        image: resolveImage({ rawVal: image, page }),
                        link: resolveLink({ rawVal: link, page, pageModels }),
                        menuDescription:
                          menuDescription && resolveRTC({ rawVal: menuDescription[0] }),
                        linkStyle: linkStyle && linkStyle.replace(/"/g, ''),
                      };
                    })
                  : {
                      image: resolveImage({ rawVal: menuItem.image, page, pageModels }),
                      link: resolveLink({ rawVal: menuItem.link, page, pageModels }),
                      menuDescription:
                        menuItem.menuDescription &&
                        resolveRTC({ rawVal: menuItem.menuDescription[0] }),
                      linkStyle: menuItem.linkStyle && menuItem.linkStyle.replace(/"/g, ''),
                    },
              };
            }),
          };
        }),
      menuList:
        menuListRef &&
        menuListRef.map((itemRef: any) => {
          const sectionDocument: Br.Content = page?.getContent(itemRef);
          const { name, primaryNavigationLink, menuGroup } = sectionDocument?.getData<any>();

          sectionDocuments.current.push({ name, document: sectionDocument });

          return {
            name,
            primaryNavigationLink: resolveLink({
              rawVal: primaryNavigationLink,
              page,
              pageModels,
            }),
            menuGroup: menuGroup.map((group: any) => {
              const { verticalDivider, contentList, fullWidth, containerBackground } = group;
              return {
                verticalDivider,
                fullWidth: !!fullWidth,
                containerBackground,
                menuGroupList: contentList.map((content: any) => {
                  const {
                    columnStyle,
                    columnTitleDescription,
                    primaryNavigationColumnTitle,
                    variant,
                    menuItem,
                  } = content;
                  return {
                    columnStyle: columnStyle && columnStyle.replace(/"/g, ''),
                    columnTitleDescription: resolveRTC({ rawVal: columnTitleDescription }),
                    primaryNavigationColumnTitle: resolveLink({
                      rawVal: primaryNavigationColumnTitle,
                      page,
                      pageModels,
                    }),
                    variant: variant && variant.replace(/"/g, ''),
                    menuItem: menuItem.length
                      ? menuItem.map((item: any) => {
                          const { image, link, includeInFooter, menuDescription, linkStyle } = item;

                          return {
                            image: resolveImage({ rawVal: image, page /* , pageModels? */ }),
                            link: resolveLink({ rawVal: link, page, pageModels }),
                            includeInFooter: resolveBool({ rawVal: includeInFooter }),
                            menuDescription:
                              menuDescription && resolveRTC({ rawVal: menuDescription[0] }),
                            linkStyle: linkStyle && linkStyle.replace(/"/g, ''),
                          };
                        })
                      : {
                          image: resolveImage({ rawVal: menuItem.image, page, pageModels }),
                          link: resolveLink({ rawVal: menuItem.link, page, pageModels }),
                          includeInFooter: resolveBool({ rawVal: menuItem.includeInFooter }),
                          menuDescription:
                            menuItem.menuDescription &&
                            resolveRTC({ rawVal: menuItem.menuDescription[0] }),
                          linkStyle: menuItem.linkStyle && menuItem.linkStyle.replace(/"/g, ''),
                        },
                  };
                }),
              };
            }),
          };
        }),
      searchSection: {
        popularSearchTerms: searchContentDocumentData?.stringList || [],
      },
    };
  }, [
    loginGroupRef,
    menuListRef,
    page,
    pageModels,
    sectionDocuments,
    appsDocumentData,
    myAccountData,
    searchContentDocumentData,
  ]);

  const listButtonClassName = useMemo(
    () => clsx(classes.listButtonClass, isPreview && 'is-preview'),
    [classes.listButtonClass, isPreview],
  );
  const sectionButtonClasses = useMemo(
    () => clsx(classes.sectionButtonClass, isPreview && 'is-preview'),
    [classes.sectionButtonClass, isPreview],
  );

  const { 'link-list-title-style': linkListTitleStyle, 'variant-label': datalayerVariant } =
    useMemo(() => componentParameterMap, [componentParameterMap]);

  const renderNewsRibbonContentButton = useCallback(
    () => (
      <div className={clsx(classes.contentButtonClass, isPreview && 'is-preview')}>
        {ribbonDocument && <BrManageContentButton content={ribbonDocument} />}
      </div>
    ),
    [classes.contentButtonClass, isPreview, ribbonDocument],
  );

  const newsRibbonContentButton = useMemo(
    () => ({
      content: renderNewsRibbonContentButton(),
    }),
    [renderNewsRibbonContentButton],
  );

  const hasNewsRibbon = useMemo(() => !!Object.keys(ribbonData).length, [ribbonData]);

  const newsRibbonProps = useMemo(() => {
    if (!hasNewsRibbon) return {};

    return {
      ...NEWS_RIBBON_MAPPER({
        params: componentParameterMap,
        document: ribbonData,
        isTransparent,
      }),
      contentButtons: isPreview && newsRibbonContentButton,
      isGlobalNewsRibbon: true,
      parentTitle: newsRibbonTitle,
    };
  }, [
    componentParameterMap,
    hasNewsRibbon,
    isPreview,
    isTransparent,
    newsRibbonContentButton,
    ribbonData,
    newsRibbonTitle,
  ]);

  const ribbonHash = useMemo(
    () =>
      hashObject({
        content: {
          detailContent: newsRibbonProps?.detailContent,
          collapsible: newsRibbonProps?.collapsible,
        },
        keys: ['detailContent', 'collapsible'],
      }),
    [newsRibbonProps?.collapsible, newsRibbonProps?.detailContent],
  );

  const prioritizeGlobalNewsRibbon = useMemo(
    () =>
      resolveBool({
        rawVal: componentParameterMap?.prioritizeGlobalNewsRibbon,
      }),
    [componentParameterMap?.prioritizeGlobalNewsRibbon],
  );

  const { elementPortalBox: newsRibbonBox } = usePortalBoxHandler({
    boxId: NEWS_RIBBON_BOX_PORTAL_ID,
    readyEventName: NEWS_RIBBON_BOX_PORTAL_EVENT,
  });

  const newsRibbonComponent = useMemo(
    () => (
      <NewsRibbonWrapper hash={ribbonHash} type="global" useGlobal={prioritizeGlobalNewsRibbon}>
        <NewsRibbon {...newsRibbonProps} />
      </NewsRibbonWrapper>
    ),
    [newsRibbonProps, prioritizeGlobalNewsRibbon, ribbonHash],
  );

  const marketoContent = {
    formId: marketoData?.marketoFormId,
    confirmationMessage: resolveRTC({ rawVal: marketoData?.confirmationMessage }),
    rules: marketoData?.rules,
    heading: marketoData?.heading,
    formDescription: resolveRTC({ rawVal: marketoData?.description }),
    emailsBlacklisted: resolveBlacklistUrls({ rawVal: marketoData?.emailBlacklistUrls }),
    CRMSyncFieldnames: resolveCRMSyncs<ICRMSyncFieldnames>({
      rawVal: marketoData?.crmSyncs,
    }),
  };

  const showCustomAccountSlotData = !registrationStatus.isApproved;

  const sessionConfigProps = useMemo(
    () => ({
      userApproved: registrationStatus.isApproved,
      isUserLogged: isLoggedIn,
      isUserBetaParticipant: Boolean(contactInfo?.betaParticipantStatus || null),
      userInfo: pick(['fullName'], userSessionInfo) as any, // picking only the props required for web components
    }),
    [
      userSessionInfo,
      isLoggedIn,
      registrationStatus.isApproved,
      contactInfo?.betaParticipantStatus,
    ],
  );

  const ctaLinkProps = useMemo(() => {
    const ctaLink = resolveLink({
      rawVal: isPortal
        ? portalCtaDocumentData?.primaryNavigationLink
        : ctaDocumentData?.primaryNavigationLink,
      page,
      pageModels,
    });

    return ctaLink;
  }, [ctaDocumentData, page, pageModels, isPortal, portalCtaDocumentData]);

  return (
    <>
      {hasNewsRibbon &&
        (!isPreview
          ? newsRibbonBox && createPortal(newsRibbonComponent, newsRibbonBox, 'newsRibbon')
          : newsRibbonComponent)}
      <GlobalHeader
        elasticsearchConfig={elasticsearchConfig}
        // web-components don't use marketo stuff.
        marketoConfig={marketoConfig}
        marketoLabels={marketoLabels}
        marketoContent={marketoContent}
        config={config}
        labels={labels}
        hasProgressBar={isBlogDetail}
        isTransparent={isTransparent}
        menuList={globalHeaderProps?.menuList}
        loginSection={globalHeaderProps?.loginSection}
        languageList={languageList}
        linkListTitleStyle={linkListTitleStyle}
        datalayerVariant={datalayerVariant}
        accountSection={globalHeaderProps?.accountSection}
        appsSection={globalHeaderProps?.appsSection}
        loginClicked={handleLoginClicked}
        logoutClicked={handleLogoutClicked}
        showCustomAccountSlotData={showCustomAccountSlotData}
        languageConfig={languageConfig}
        sessionConfig={sessionConfigProps}
        searchSection={globalHeaderProps?.searchSection}
        ctaLink={ctaLinkProps}
      >
        <MessageBar
          isOpen={showErrorMessage}
          autoHideDuration={4000}
          onClose={() => setShowErrorMessage(false)}
          message={labels?.sso_interruption_message_label || ''}
          severity="error"
        />
        {isPreview && (
          <>
            <ContentButton
              slotName="menuList"
              document={listDocument}
              className={listButtonClassName}
            />
            {sectionDocuments?.current.map((section: any, idx) => (
              <ContentButton
                // eslint-disable-next-line react/no-array-index-key
                key={`content-button-${idx}`}
                slotName={section.name}
                document={section.document}
                className={sectionButtonClasses}
              />
            ))}
          </>
        )}
      </GlobalHeader>
      <PortalNavigationBox />
    </>
  );
};

const gtmConfig = { eventName: 'gtm:click:link', isCustom: true };
const GlobalHeaderContainerLegacy = withGTM({
  Component: GlobalHeaderContainer,
  onDatalayer: onDatalayerClickLegacy,
  config: gtmConfig,
});
const GlobalHeaderContainerSearch = withGTM({
  Component: GlobalHeaderContainerLegacy,
  onDatalayer: onDatalayerCustom,
  config: { eventName: 'gtm:custom:search', isCustom: true },
});
const GlobalHeaderContainerGlobal = withGTM({
  Component: GlobalHeaderContainerSearch,
  onDatalayer: onDatalayerClickLink,
  config: gtmConfig,
});
const GlobalHeaderContainerGTM = withGTM({
  Component: GlobalHeaderContainerGlobal,
  onDatalayer: onDatalayerClickCTA,
  config: { eventName: 'gtm:click:cta', isCustom: true },
});

export default memo(GlobalHeaderContainerGTM);
